// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { provideProductCarouselContext } from "@west/App/Components/ProductSupportVariants/ProductCarouselSupportVariants/ProductCarouselProductContext.helper";
import updateCarouselProductsVariantSelection from "@west/App/Handlers/ProductCarousel/UpdateCarouselProductsVariantSelection";
import updateProductInfoVariantChild from "@west/App/Handlers/ProductCarousel/UpdateProductInfoVariantChild";
import useLoadVariantsProduct from "@west/App/Hooks/ProductSupportVariants/useLoadVariantsProduct";
import { CustomAppState } from "@west/App/Store/Reducers";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

/**
 * XNG-Changes: Support variants in PLP
 * Make interface export
 */
export interface OwnProps {
    carouselId: string;
    product: ProductModel;
}

/**
 * XNG-Changes: Support variants in PLP
 * use provideProductContext method to return product with/without variant product
 */
const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const { product, productInfo, parentProductId, isOriginalProduct } = provideProductCarouselContext(state, ownProps);

    return {
        product,
        parentProduct: ownProps.product,
        productInfo,
        productVariantOptions: {
            parentProductId,
            isOriginalProduct,
        },
        // check if initial carousel product's variants are loaded
        isVariantProductLoaded:
            parentProductId &&
            state.xProductCarouselVariantChildren.variantChildren[ownProps.carouselId]?.[parentProductId],
    };
};

const mapDispatchToProps = {
    updateCarouselProductsVariantSelection,
    updateProductInfo,
    updateProductInfoVariantChild,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const ProductCarouselProductContext: FC<Props> = ({
    product,
    productInfo,
    children,
    carouselId,
    parentProduct,
    productVariantOptions: { parentProductId, isOriginalProduct },
    isVariantProductLoaded,
    updateProductInfo,
    updateProductInfoVariantChild,
}) => {
    useLoadVariantsProduct({
        parentProduct,
        parentProductId,
        carouselId,
    });

    if (!productInfo) {
        return null;
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            /**
             * The handler to update QTY when a product is parent and has variants
             */
            if (!isOriginalProduct && parentProductId && isVariantProductLoaded) {
                updateProductInfoVariantChild({
                    productId: product.id,
                    parentProductId,
                    qtyOrdered,
                    carouselId,
                });
            } else {
                updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
            }
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            /**
             * The handler to update UnitOfMeasure when a product is parent and has variants
             */
            if (!isOriginalProduct && parentProductId && isVariantProductLoaded) {
                updateProductInfoVariantChild({
                    productId: product.id,
                    unitOfMeasure,
                    parentProductId,
                    carouselId,
                });
            } else {
                updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
            }
        },
    };

    return <ProductContext.Provider value={productContext}>{children}</ProductContext.Provider>;
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductContext);
